
<template lang="pug">
  .hero(v-lazy:background-image="this.image", :class="heroClass")
    .video(v-if="showVideo")
      video-embed(
        :video="this.video",
        :loop-start="Number(this.videoLoopStart)",
        :loop-end="Number(this.videoLoopEnd)"
      )
    .overlay(v-if="showOverlay" :style="overlayStyle")
    .container(:style="containerStyle")
      .hero__content(:style="textStyle")
        slot
</template>

<script>
  import VideoEmbed from 'scripts/components/video/VideoEmbed.vue';

  export default {
    name: 'Hero',
    components: {
      VideoEmbed,
    },
    props: {
      imageDesktop: String,
      imageMobile: String,
      video: String,
      videoLoopStart: String,
      videoLoopEnd: String,
      minHeight: {
        type: String,
        default: '50vh',
      },
      overlayColor: {
        type: String,
        default: null,
      },
      overlayOpacity: {
        type: String, // Range slider value gets passed as string, needs to be converted to num when used
        default: '0',
      },
      textAlign: {
        type: String,
        default: 'center',
      },
      textPositionVertical: {
        type: String,
        default: 'center',
      },
      textPositionHorizontal: {
        type: String,
        default: 'center',
      },
      textColor: {
        type: String,
        default: null,
      },
    },
    computed: {
      showVideo() {
        return this.video && ['desktop', 'hd'].includes(this.$mq);
      },
      showOverlay() {
        return this.overlayColor && this.hasImage;
      },
      image() {
        if (['mobile', 'tablet'].includes(this.$mq) && this.imageMobile) {
          return this.imageMobile;
        }
        else if (this.imageDesktop) {
          return this.imageDesktop;
        }
        else {
          return null;
        }
      },
      hasImage () {
        return Boolean(this.image);
      },
      heroClass () {
        return {
          'hero--simple': !this.hasImage,
          'hero--overlay': this.overlayColor,
        };
      },
      heroStyle () {
        return {
          'background-image': (this.hasImage) ? `url(${this.image})` : 'none',
        }
      },
      overlayStyle () {
        return {
          'background-color': this.overlayColor,
          'opacity': Number(this.overlayOpacity),
        }
      },
      containerStyle () {
        if (['desktop', 'hd'].includes(this.$mq)) {
          return {
            'justify-content': this.textPositionHorizontal,
            'align-items': this.textPositionVertical,
            'min-height': (this.hasImage) ? this.minHeight : '0',
          };
        }

        return {
          'justify-content': 'center',
          'align-items': 'center',
          'min-height': 0,
        };
      },
      textStyle () {
        if (['desktop', 'hd'].includes(this.$mq)) {
          return {
            'text-align': this.textAlign,
            'color': this.textColor,
          }
        }

        return {
          'text-align': 'center',
          'color': undefined,
        };
      },
    },
  };
</script>

<style scoped lang="scss">
  .hero {
    $hero-mobile-image-height: 234px;

    background-size: 0%;
    text-align: center;
    position: relative;
    /* margin-bottom: $grid-gutter; */

    @include desktop-up {
      position: relative;
      min-height: 539px;
      /* margin-bottom: 2*$grid-gutter; */

      margin-left: auto;
      margin-right: auto;
      padding-bottom: 0;

      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;

      background-position: center center;
      background-size: cover;
      background-repeat: no-repeat;

      &.hero--collection {
        min-height: 450px;
      }
    }

    .video,
    .overlay {
      z-index: 1;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: $hero-mobile-image-height;

      @include desktop-up {
        height: 100%;
      }
    }

    .container {
      padding-top: 2*$grid-gutter;
      padding-bottom: 0;//2*$grid-gutter;
      display: flex;

      width: 100%;

      @include desktop-up {
        padding-top: 50px;
        padding-bottom: 50px;
        padding-left: 3*$grid-gutter;
        padding-right: 3*$grid-gutter;
      }

      .hero__content {
        z-index: 2; // Place above the overlay

        @include mobile-and-tablet-only {
          padding-bottom: 2.5rem;

          h1 {
            color: $color-indigo;
          }
        }

        h2 {
          margin-bottom: $grid-gutter;
        }

        p {
          margin-bottom: 2*$grid-gutter;
        }
      }
    }

    &__content {
      max-width: 635px;

      *:first-child {
        margin-top: 0;
      }

      *:last-child {
        margin-bottom: 0;
      }

      .hero--simple & {
        max-width: 700px;
      }
    }

    &::before {
      content: '';
      display: block;
      background-image: inherit;
      background-position: center center;
      background-size: cover;
      background-repeat: no-repeat;

      height: $hero-mobile-image-height;

      @include desktop-up {
        display: none;
      }
    }

    &.hero--simple {
      display: block;
      margin-bottom: 0;

      @include desktop-up {
        min-height: 0;
        margin-bottom: 0;
      }

      .container {
        padding-top: $grid-gutter;
        padding-bottom: $grid-gutter;

        @include tablet-up {
          padding-top: 2*$grid-gutter;
          padding-bottom: 2*$grid-gutter;
        }
      }

      &.hero--overlay {
        padding-bottom: 0;
        margin-bottom: 40px;

        .overlay {
          height: 100%;
        }

        // <slot> content last child margin shouldn't interfere with layout
        .hero__content {
          > :last-child,
          > :last-child > :last-child {
            margin-bottom: 0;
          }
        }
      }

      &::before {
        display: none;
      }
    }

  }

  ::v-deep .h1--homepage {
    @include desktop-up {
      font-size: rem(56);
    }
  }
</style>
